/**
 * ENG-2624 — keyboard-delete decision for the P&ID editor's single active
 * selection (the object open in the Node Details panel).
 *
 * Multi-selection delete (Select mode) is owned by PnidRenderer's own keydown
 * handler over its `selectedRectangles` set. This helper governs the *other*
 * case — the default single-select flow — and deliberately declines when a
 * multi-selection exists so the two handlers never both fire on one keypress
 * (their selection sets are kept in sync, so a non-empty multi-selection means
 * PnidRenderer will handle the key).
 */
export interface SingleDeleteKeyContext {
  /** KeyboardEvent.key */
  key: string
  /** event.target.tagName (case-insensitive) */
  targetTag: string
  /** event.target.isContentEditable */
  isContentEditable: boolean
  /** Whether an object is currently selected (Node Details panel open). */
  hasActiveSelection: boolean
  /** Size of the Select-mode multi-selection. */
  multiSelectionCount: number
  /** Editing disabled (viewer / embed). */
  isReadOnly: boolean
}

/**
 * True when a keydown should delete the single active selection. Only Delete and
 * Backspace qualify, and only when: something is selected, editing is allowed,
 * the user isn't typing into a form control, and there is NO multi-selection.
 */
export function shouldDeleteActiveSelection(ctx: SingleDeleteKeyContext): boolean {
  if (ctx.key !== 'Delete' && ctx.key !== 'Backspace') return false
  if (!ctx.hasActiveSelection) return false
  if (ctx.isReadOnly) return false
  // A live multi-selection is PnidRenderer's job — declining here avoids a
  // double delete on the same keypress.
  if (ctx.multiSelectionCount > 0) return false
  const tag = ctx.targetTag.toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || ctx.isContentEditable) {
    return false
  }
  return true
}
